import React, { useEffect, useState, useRef, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SpotlightAuthor } from '../../data/homePageData';
import UnifiedBookCard from '../Books/UnifiedBookCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { handleImgError } from '../../utils/imageUtils';
interface AuthorSpotlightCarouselProps {
  authors: SpotlightAuthor[];
}
export const AuthorSpotlightCarousel: React.FC<AuthorSpotlightCarouselProps> = ({
  authors
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  // Auto-advance
  useEffect(() => {
    if (isHovered || authors.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % authors.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [isHovered, authors.length]);
  const handlePrev = () => {
    setCurrentIndex(prev => prev === 0 ? authors.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % authors.length);
  };
  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };
  if (!authors || authors.length === 0) return null;
  const arrowBgClass = isAfterTide ? 'bg-[#1a1a2e]/90 text-[#f5f0e8] border-[#16213e]' : dk.isDark ? 'bg-gray-800/90 text-gray-200 border-gray-700' : 'bg-white/90 text-gray-700 border-gray-200';
  const activeDotClass = isAfterTide ? 'bg-[#e2b04a]' : rt.bgPrimary;
  const inactiveDotClass = dk.isDark ? 'bg-gray-600' : 'bg-gray-300';
  return <div className="relative group" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-1711" data-mp="23tzll51w9fxk">
      <div className={`rounded-xl shadow-md overflow-hidden border ${dk.card} relative`} data-id="element-1712" data-mp="2dmngtvd87bu6">
        {/* Carousel Container */}
        <div className="flex transition-transform duration-500 ease-in-out" style={{
        transform: `translateX(-${currentIndex * 100}%)`
      }} data-id="element-1713" data-mp="1l3q82z0nq1qk">
          {authors.map(author => <div key={author.id} className="w-full flex-shrink-0 p-6" data-id="element-1714" data-mp="18myyq4w42y5n">
              <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-1715" data-mp="13m1gxtf0k64o">
                <div className="flex items-center gap-4 md:w-1/3" data-id="element-1716" data-mp="ynhtfokruma7">
                  <img src={author.image} alt={author.name} className="h-20 w-20 rounded-full object-cover flex-shrink-0" loading="lazy" onError={handleImgError} data-id="element-1717" data-mp="yn81hvg6251v" />
                  <div data-id="element-1718" data-mp="kwl549xvor8y">
                    <h3 className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-1719" data-mp="ktz0oj6aaruy">
                      {author.name}
                    </h3>
                    <p className={`text-sm line-clamp-2 mt-1 ${dk.textSecondary}`} data-id="element-1720" data-mp="ayf37sc2nl43">
                      {author.bio}
                    </p>
                    <Link to={`/author/${author.id}`} className={`inline-block mt-2 text-sm font-medium ${rt.accentLink}`} data-id="element-1721" data-mp="vn7y8bqta6ig">
                      View Profile →
                    </Link>
                  </div>
                </div>
                <div className="md:w-2/3" data-id="element-1722" data-mp="qw7yyzu7zccs">
                  <h4 className={`text-sm font-semibold uppercase mb-3 ${dk.textMuted}`} data-id="element-1723" data-mp="quwxj6o8ygou">
                    Author's Books
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1724" data-mp="hwkyqhd88clb">
                    {[{
                  ...author.books.debut,
                  author: author.name,
                  isDebut: true
                }, {
                  ...author.books.mostPopular,
                  author: author.name
                }, {
                  ...author.books.latest,
                  author: author.name
                }, {
                  ...author.books.leastReviewed,
                  author: author.name
                }].map(book => <UnifiedBookCard key={book.id} book={book} data-id="element-1725" data-mp="fp5dn8kdw5f" />)}
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Desktop Navigation Arrows */}
        {authors.length > 1 && <>
            <button onClick={handlePrev} className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md border ${arrowBgClass} 
                hidden md:flex items-center justify-center p-2
                opacity-0 group-hover:opacity-100 transition-opacity duration-200`} aria-label="Previous author" data-id="element-1726" data-mp="5f1tebb6999l">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-1727" data-mp="2g6ju0d8z0r3z" />
            </button>
            <button onClick={handleNext} className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md border ${arrowBgClass} 
                hidden md:flex items-center justify-center p-2
                opacity-0 group-hover:opacity-100 transition-opacity duration-200`} aria-label="Next author" data-id="element-1728" data-mp="k2vwce5kpavr">
              <ChevronRightIcon className="h-5 w-5" data-id="element-1729" data-mp="130zvqgvnrvkd" />
            </button>
          </>}
      </div>

      {/* Dot Indicators */}
      {authors.length > 1 && <div className="flex justify-center gap-2 mt-4" data-id="element-1730" data-mp="17xqan0vq8l47">
          {authors.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentIndex ? activeDotClass : inactiveDotClass}`} aria-label={`Go to author ${index + 1}`} data-id="element-1731" data-mp="1csm9ajvgy6qa" data-mpi={index} />)}
        </div>}
    </div>;
};
export default AuthorSpotlightCarousel;